import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import { Prompt, PromptType } from '@dazzle/api-client';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';

import { promptFetch, promptUpdate } from 'Common/api/DazzleInternalService';

const PromptTypeNames: Partial<{ [key in PromptType]: string }> = {
  [PromptType.ChatSystemPrompt]: 'Chat System Prompt',
  [PromptType.MemorySystemPrompt]: 'Memory System Prompt',
  [PromptType.MemoryUserPrompt]: 'Memory User Prompt',
  [PromptType.FreeformIntentSystemPrompt]: 'Freeform Intent Classification Prompt',
  [PromptType.IntentSystemPrompt]: 'Intent Classification Prompt',
  [PromptType.IntentExtractCalendar]: 'Intent Extract - Calendar',
  [PromptType.IntentExtractContact]: 'Intent Extract - Contact',
  [PromptType.IntentExtractOccasions]: 'Intent Extract - Occasions',
  [PromptType.IntentExtractProduct]: 'Intent Extract - Product',
  [PromptType.IntentExtractFood]: 'Intent Extract - Food',
  [PromptType.IntentExtractFix]: 'Intent Extract - Fix',
  [PromptType.IntentExtractIdentify]: 'Intent Extract - Identify',
  [PromptType.IntentExtractTranscribe]: 'Intent Extract - Transcribe',
  [PromptType.IntentExtractRemind]: 'Intent Extract - Remind',
  [PromptType.IntentExtractFollowup]: 'Intent Extract - Followup',
  [PromptType.IntentExtractShareableMoment]: 'Intent Extract - Shareable Moment',
  [PromptType.IntentExtractCoordinate]: 'Intent Extract - Coordinate',
  [PromptType.IntentExtractReferenceOrStatus]: 'Intent Extract - Reference/Status',
  [PromptType.IntentExtractQrcode]: 'Intent Extract - QR Code',
  [PromptType.IntentExtractFile]: 'Intent Extract - File',
  [PromptType.IntentExtractHealth]: 'Intent Extract - Health',
  [PromptType.TitleExtractSharePhotos]: 'Title Extract - Share Photos',
  [PromptType.PhotoCaption]: 'Photo Caption',
  [PromptType.PhotoIdExtraction]: 'Photo ID Extraction',
  [PromptType.PhotoKeywordsExtraction]: 'Photo Groups - Keywords Extraction',
  [PromptType.OnboardingProductIntro]: 'Onboarding - Product Intro',
  [PromptType.OnboardingNameRequest]: 'Onboarding - Name Request',
  [PromptType.OnboardingNameExtraction]: 'Onboarding - Name Extraction',
  [PromptType.OnboardingNameComment]: 'Onboarding - Name Comment',
  [PromptType.OnboardingStarSignGuess]: 'Onboarding - Star Sign Guess',
  [PromptType.OnboardingStarSignGame]: 'Onboarding - Star Sign Game',
  [PromptType.OnboardingStarSignInvitation]: 'Onboarding - Star Sign Invitation',
  [PromptType.OnboardingAppDownload]: 'Onboarding - App Download',
  [PromptType.OnboardingFlightIntro]: 'Onboarding - Flight Intro',
  [PromptType.OnboardingFlightExampleIntro]: 'Onboarding - Flight Example Intro',
  [PromptType.OnboardingFlightExampleOutro]: 'Onboarding - Flight Example Outro',
  [PromptType.OnboardingPhotoPrompt]: 'Onboarding - Photo Prompt',
  [PromptType.OnboardingAppInstallAgent]: 'Onboarding - App Install Agent',
  [PromptType.OnboardingPermissionAgent]: 'Onboarding - Permission Agent',
  [PromptType.ProfileSystemPrompt]: 'Profile System Prompt',
  [PromptType.OrderChatAgentPrompt]: 'Order Chat Agent Prompt',
  [PromptType.OrderIntentClassifierPrompt]: 'Order Intent Classifier Prompt',
  [PromptType.PhotoGroupingSystemPrompt]: 'Photo Groups - System Prompt',
  [PromptType.IdeasCuratedSystemPrompt]: 'Ideas - Curated System Prompt',
  [PromptType.IdeasRerankPrompt]: 'Ideas - Rerank',
  [PromptType.IdeasFashionProfilePrompt]: 'Ideas - Fashion & Style Profile',
  [PromptType.IdeasTravelProfilePrompt]: 'Ideas - Travel Profile',
  [PromptType.IdeasActivitiesProfilePrompt]: 'Ideas - Activities Profile',
  [PromptType.IdeasRestaurantsProfilePrompt]: 'Ideas - Restaurants Profile',
  [PromptType.IdeasHotelsProfilePrompt]: 'Ideas - Hotels Profile',
  [PromptType.IdeasLearningProfilePrompt]: 'Ideas - Learning Profile',
  [PromptType.IdeasGiftsProfilePrompt]: 'Ideas - Gifts Profile',
  [PromptType.IdeasGadgetsProfilePrompt]: 'Ideas - Gadgets Profile',
  [PromptType.IdeasConcertsProfilePrompt]: 'Ideas - Concerts Profile',
  [PromptType.IdeasPlaylistsProfilePrompt]: 'Ideas - Playlists Profile',
  [PromptType.IdeasPersonalCareProfilePrompt]: 'Ideas - Self-Care Profile',
  [PromptType.IdeasClassesProfilePrompt]: 'Ideas - Classes Profile',
  [PromptType.IdeasPodcastsProfilePrompt]: 'Ideas - Podcasts Profile',
  [PromptType.ProfileStageHomeTemplate]: 'Profile Stage - Home',
  [PromptType.ProfileStageTripsTemplate]: 'Profile Stage - Trips',
  [PromptType.ProfileStagePeopleTemplate]: 'Profile Stage - People',
  [PromptType.ProfileStagePeopleQuestions]: 'Profile Stage - People Questions',
  [PromptType.ProfileStageHomeLivingTemplate]: 'Profile Stage - Home Living',
  [PromptType.ProfileStageHomeLivingQuestions]: 'Profile Stage - Home Living Questions',
  [PromptType.ProfileStageActivitiesTemplate]: 'Profile Stage - Activities',
  [PromptType.ProfileStageActivitiesQuestions]: 'Profile Stage - Activities Questions',
  [PromptType.ProfileStageActivitiesCombiner]: 'Profile Stage - Activities Combiner',
  [PromptType.ProfileStageFashionStyleTemplate]: 'Profile Stage - Fashion & Style',
  [PromptType.ProfileStageFashionStyleQuestions]: 'Profile Stage - Fashion & Style Questions',
  [PromptType.ProfileStageFashionStyleCombiner]: 'Profile Stage - Fashion & Style Combiner',
  [PromptType.ProfileStageBrandingTemplate]: 'Profile Stage - Branding',
  [PromptType.ProfileStageBrandingQuestions]: 'Profile Stage - Branding Questions',
  [PromptType.ProfileStageSpendingTemplate]: 'Profile Stage - Spending',
  [PromptType.ProfileStageDefaultCombiner]: 'Profile Stage - Default Combiner',
};

const otherPrompts: string[] = [];

const IDEAS_OUTPUT_SCHEMA = `{
  "ideas": [
    {
      "title": "string",
      "text": "string",
      "confidence": "float",
      "sources": ["string"],
      "cover_photo_id": "string | null",
      "search_query": "string",
      "search_keyword": "string",
      "follow_up": "string",
      "url": "string | null",
      "event_date": "string | null",
      "event_time": "string | null",
      "event_location": "string | null",
      "button_text": "string",
      "justification": "string"
    }
  ]
}`;

const PHOTO_GROUPS_OUTPUT_SCHEMA = `{
  "groups": [
    {
      "photo_ids": ["string"],
      "group_title": "string",
      "suggested_tag": "string",
      "description": "string",
      "confidence_score": "float",
      "theme": "string | null",
      "temporal_pattern": "string | null",
      "activity": "string",
      "periodicity_insight": "string",
      "context_analysis": "string",
      "importance_reason": "string",
      "inferred_interests": "string",
      "tavily_queries": ["string"]
    }
  ]
}`;

const PromptCategories = {
  chat: {
    title: 'Chat & Memory',
    types: [
      PromptType.ChatSystemPrompt,
      PromptType.MemorySystemPrompt,
      PromptType.MemoryUserPrompt,
      PromptType.ProfileSystemPrompt,
    ],
  },
  intent: {
    title: 'Intent Classification & Extraction',
    types: [
      PromptType.FreeformIntentSystemPrompt,
      PromptType.IntentSystemPrompt,
      PromptType.IntentExtractCalendar,
      PromptType.IntentExtractContact,
      PromptType.IntentExtractOccasions,
      PromptType.IntentExtractProduct,
      PromptType.IntentExtractFood,
      PromptType.IntentExtractFix,
      PromptType.IntentExtractIdentify,
      PromptType.IntentExtractTranscribe,
      PromptType.IntentExtractRemind,
      PromptType.IntentExtractFollowup,
      PromptType.IntentExtractShareableMoment,
      PromptType.IntentExtractCoordinate,
      PromptType.IntentExtractReferenceOrStatus,
      PromptType.IntentExtractQrcode,
      PromptType.IntentExtractFile,
      PromptType.IntentExtractHealth,
      PromptType.TitleExtractSharePhotos,
    ],
  },
  photo: {
    title: 'Photo Description',
    types: [
      PromptType.PhotoCaption,
      PromptType.PhotoIdExtraction,
    ],
  },
  ideas: {
    title: 'Ideas',
    types: [
      PromptType.IdeasCuratedSystemPrompt,
      PromptType.IdeasRerankPrompt,
      PromptType.IdeasFashionProfilePrompt,
      PromptType.IdeasTravelProfilePrompt,
      PromptType.IdeasActivitiesProfilePrompt,
      PromptType.IdeasRestaurantsProfilePrompt,
      PromptType.IdeasHotelsProfilePrompt,
      PromptType.IdeasLearningProfilePrompt,
      PromptType.IdeasGiftsProfilePrompt,
      PromptType.IdeasGadgetsProfilePrompt,
      PromptType.IdeasConcertsProfilePrompt,
      PromptType.IdeasPlaylistsProfilePrompt,
      PromptType.IdeasPersonalCareProfilePrompt,
      PromptType.IdeasClassesProfilePrompt,
      PromptType.IdeasPodcastsProfilePrompt,
    ],
  },
  photoGroups: {
    title: 'Photo Groups',
    types: [
      PromptType.PhotoGroupingSystemPrompt,
      PromptType.PhotoKeywordsExtraction,
    ],
  },
  order: {
    title: 'Order',
    types: [PromptType.OrderChatAgentPrompt, PromptType.OrderIntentClassifierPrompt],
  },
  profile: {
    title: 'Profile Stages',
    types: [
      PromptType.ProfileStageHomeTemplate,
      PromptType.ProfileStageTripsTemplate,
      PromptType.ProfileStagePeopleTemplate,
      PromptType.ProfileStagePeopleQuestions,
      PromptType.ProfileStageHomeLivingTemplate,
      PromptType.ProfileStageHomeLivingQuestions,
      PromptType.ProfileStageActivitiesTemplate,
      PromptType.ProfileStageActivitiesQuestions,
      PromptType.ProfileStageActivitiesCombiner,
      PromptType.ProfileStageFashionStyleTemplate,
      PromptType.ProfileStageFashionStyleQuestions,
      PromptType.ProfileStageFashionStyleCombiner,
      PromptType.ProfileStageBrandingTemplate,
      PromptType.ProfileStageBrandingQuestions,
      PromptType.ProfileStageSpendingTemplate,
      PromptType.ProfileStageDefaultCombiner,
    ],
  },
  onboarding: {
    title: 'Onboarding',
    types: [
      PromptType.OnboardingProductIntro,
      PromptType.OnboardingNameRequest,
      PromptType.OnboardingNameExtraction,
      PromptType.OnboardingNameComment,
      PromptType.OnboardingStarSignGuess,
      PromptType.OnboardingStarSignGame,
      PromptType.OnboardingStarSignInvitation,
      PromptType.OnboardingAppDownload,
      PromptType.OnboardingFlightIntro,
      PromptType.OnboardingFlightExampleIntro,
      PromptType.OnboardingFlightExampleOutro,
      PromptType.OnboardingPhotoPrompt,
      PromptType.OnboardingAppInstallAgent,
      PromptType.OnboardingPermissionAgent,
    ],
  },
  other: {
    title: 'Uncategorized',
    types: otherPrompts as PromptType[]
  }
};

const toSlug = (value: string): string =>
  value.replace(/_/g, '-').toLowerCase();

const getInitialExpandedSections = (hash: string): Record<string, boolean> => {
  const defaults: Record<string, boolean> = {
    chat: true,
    intent: true,
    photo: true,
    profile: false,
    onboarding: false,
    order: false,
    photoGroups: true,
    ideas: true,
    other: false
  };
  if (!hash) return defaults;
  for (const [key, category] of Object.entries(PromptCategories)) {
    if (key === hash || category.types.some((t) => toSlug(t) === hash)) {
      defaults[key] = true;
      break;
    }
  }
  return defaults;
};

const PromptEditorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialHash = location.hash.slice(1);
  const [prompts, setPrompts] = useState<Prompt[] | undefined>(undefined);
  const [originalTexts, setOriginalTexts] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    () => getInitialExpandedSections(initialHash)
  );
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (!prompts || hasScrolled.current) return;
    const hash = location.hash.slice(1);
    if (!hash) return;
    hasScrolled.current = true;
    setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [prompts, location.hash]);

  const fetchPromptsInternal = async () => {
    const response = await promptFetch();
    if (!response) {
      return;
    }
    const originals: Record<string, string> = {};
    for (const prompt of response.prompts) {
      if (!PromptTypeNames[prompt.prompt_type] && !otherPrompts.includes(prompt.prompt_type)) {
        otherPrompts.push(prompt.prompt_type);
      }
      originals[prompt.prompt_type] = prompt.prompt;
    }
    setOriginalTexts(originals);
    setPrompts(response.prompts);
  };

  if (prompts === undefined) {
    void fetchPromptsInternal();
    return null;
  }

  const onPromptChange = (prompt: Prompt, newPrompt: string) => {
    setPrompts(prompts.map((p) =>
      p.prompt_type === prompt.prompt_type ? { ...p, prompt: newPrompt } : p
    ));
  };

  const onSubmit = async (prompt: Prompt) => {
    setSubmitting(true);
    const response = await promptUpdate(prompt.prompt_type, prompt.prompt);
    if (response) {
      setPrompts((prev) =>
        prev?.map((p) =>
          p.prompt_type === prompt.prompt_type
            ? { ...p, last_saved_ts: response.last_saved_ts }
            : p
        )
      );
      setOriginalTexts((prev) => ({ ...prev, [prompt.prompt_type]: prompt.prompt }));
    }
    setSubmitting(false);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const copyDeepLink = (hash: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#${hash}`;
    void navigator.clipboard.writeText(url);
    navigate(`#${hash}`, { replace: true });
  };

  const getPromptsForCategory = (types: PromptType[]) => {
    return prompts.filter((p) => types.includes(p.prompt_type));
  };

  return <>
    <CompassHeader pageType={PageType.PromptEditor} />
    <Page>
      <PromptsContainer>
        {Object.entries(PromptCategories).map(([key, category]) => {
          const categoryPrompts = getPromptsForCategory(category.types);
          const isExpanded = expandedSections[key];

          if (categoryPrompts.length === 0) {
            return null;
          }

          return (
            <Section key={key} id={key}>
              <SectionHeader onClick={() => toggleSection(key)}>
                <SectionTitle>
                  {category.title}
                  <PromptCount>{categoryPrompts.length} prompts</PromptCount>
                  <LinkButton onClick={(e) => copyDeepLink(key, e)} title="Copy link to section">
                    🔗
                  </LinkButton>
                </SectionTitle>
                <ExpandIcon $isExpanded={isExpanded}>▼</ExpandIcon>
              </SectionHeader>
              {isExpanded && (
                <SectionContent>
                  {categoryPrompts.map((prompt) => {
                    const promptSlug = toSlug(prompt.prompt_type);
                    const isDirty = prompt.prompt !== originalTexts[prompt.prompt_type];
                    return (
                      <PromptContainer key={prompt.prompt_type} id={promptSlug}>
                        <PromptHeader>
                          <h3>{PromptTypeNames[prompt.prompt_type] ?? prompt.prompt_type}</h3>
                          <PromptLinkButton onClick={(e) => copyDeepLink(promptSlug, e)} title="Copy link to prompt">
                            🔗
                          </PromptLinkButton>
                          <PromptMeta>
                            {prompt.last_saved_ts
                              ? new Date(prompt.last_saved_ts).toLocaleString()
                              : 'Not in DB'}
                          </PromptMeta>
                        </PromptHeader>
                        <PromptTextArea
                          $isDirty={isDirty}
                          value={prompt.prompt}
                          onChange={(e) => onPromptChange(prompt, e.currentTarget.value)}
                          disabled={submitting}
                          rows={prompt.prompt_type === PromptType.ChatSystemPrompt ? 30 : 10}
                        />
                        {prompt.prompt_type === PromptType.IdeasCuratedSystemPrompt && (
                          <SchemaContainer>
                            <h4>Expected Output Schema (Pydantic Model)</h4>
                            <pre>{IDEAS_OUTPUT_SCHEMA}</pre>
                          </SchemaContainer>
                        )}
                        {prompt.prompt_type === PromptType.PhotoGroupingSystemPrompt && (
                          <SchemaContainer>
                            <h4>Expected Output Schema (Pydantic Model)</h4>
                            <pre>{PHOTO_GROUPS_OUTPUT_SCHEMA}</pre>
                          </SchemaContainer>
                        )}
                        <SaveRow>
                          <button onClick={() => onSubmit(prompt)} disabled={submitting}>
                            Save
                          </button>
                          {isDirty && <UnsavedLabel>Changes have not been saved</UnsavedLabel>}
                        </SaveRow>
                      </PromptContainer>
                    );
                  })}
                </SectionContent>
              )}
            </Section>
          );
        })}
      </PromptsContainer>
    </Page>
  </>;
};

const Page = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  align-content: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PromptsContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  flex-direction: column;
  gap: 24px;
  padding: 32px 24px;
`;

const LinkButton = styled.button`
  all: unset;
  cursor: pointer;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 2px 6px;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const PromptLinkButton = styled.button`
  all: unset;
  cursor: pointer;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 2px 6px;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 138, 255, 0.1);
  }
`;

const Section = styled.div`
  width: 100%;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #E4E7EF;
  overflow: hidden;
  transition: box-shadow 0.15s ease;
  scroll-margin-top: 16px;

  &:hover ${LinkButton} {
    opacity: 0.8;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  cursor: pointer;
  background: linear-gradient(135deg, #02033B 0%, #743D98 60%, #AC326C 100%);
  color: white;
  user-select: none;
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #060545 0%, #5D3080 60%, #8E2858 100%);
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
`;

const PromptCount = styled.span`
  font-size: 14px;
  font-weight: 400;
  opacity: 0.9;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 12px;
`;

const ExpandIcon = styled.span<{ $isExpanded: boolean }>`
  font-size: 12px;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: #F8F9FC;
`;

const PromptHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PromptContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  scroll-margin-top: 16px;

  &:hover ${PromptLinkButton} {
    opacity: 0.6;
  }

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1A1D2B;
  }

  button {
    align-self: flex-start;
    background: linear-gradient(135deg, #02033B 0%, #743D98 60%, #AC326C 100%);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 138, 255, 0.3);

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 138, 255, 0.4);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const PromptMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #8B90A0;
  margin-left: auto;
`;

const SaveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UnsavedLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #C44D18;
`;

const PromptTextArea = styled.textarea<{ $isDirty: boolean }>`
  width: 100%;
  padding: 12px;
  border: 2px solid ${(props) => (props.$isDirty ? '#E8A87C' : '#E4E7EF')};
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  background-color: ${(props) => (props.$isDirty ? '#FFF8F3' : 'white')};

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$isDirty ? '#C44D18' : '#008AFF')};
  }

  &:disabled {
    background: #F8F9FC;
    cursor: not-allowed;
  }
`;

const SchemaContainer = styled.div`
  margin-top: 12px;
  padding: 16px;
  background: #f1f3f5;
  border-radius: 8px;
  border: 1px solid #dee2e6;

  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #495057;
  }

  pre {
    margin: 0;
    font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
    font-size: 12px;
    color: #212529;
    white-space: pre-wrap;
  }
`;

export default PromptEditorPage;
